import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { athletesApi, statsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import MaraSportsLogo from '../components/MaraSportsLogo';
import { africanCountries, kenyaData, otherCountriesData, defaultSchools, defaultAcademies } from '../data/locationData';
import axios from 'axios';
import {
    Trophy, ArrowLeft, User, Shield, MapPin, School,
    Award, BarChart3, Edit, Save, X, Loader2, Plus,
    Upload, Camera, FileText, Phone, Users, Video, Sparkles
} from 'lucide-react';

// Import modular components
import {
    ProfileHeader,
    ProfilePhotoUpload,
    DocumentUpload,
    LocationFields,
    ParentGuardianFields,
    MediaConsentField,
    SportSelector,
    ProfileDetailsView
} from '../components/athlete';
import SmartDocumentUpload from '../components/athlete/SmartDocumentUpload';
import LocationPermissionPrompt from '../components/LocationPermissionPrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AthleteProfilePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, token, isAuthenticated } = useAuth();
    const [athlete, setAthlete] = useState(null);
    const [sports, setSports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);

    const [formData, setFormData] = useState({
        full_name: '',
        date_of_birth: '',
        age_category: '',
        country: 'Kenya',
        county: '',
        subcounty: '',
        city: '',
        school: '',
        academy: '',
        sports: [],
        mobile_number: '',
        bio: '',
        profile_image: '',
        birth_certificate_url: '',
        passport_url: '',  // Added passport option
        id_proof: '',
        parent_guardian_name: '',
        parent_guardian_mobile: '',
        parent_guardian_email: '',
        parent_consent: false,
        media_consent: false,
        representing_type: '',
        representing_name: '',
        reason_to_register: '',
        profile_location: null  // GPS location during profile creation
    });
    
    const isAthleteRole = user?.role === 'athlete';
    
    // Calculate age from DOB
    const calculateAge = (dob) => {
        if (!dob) return null;
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };
    
    const athleteAge = calculateAge(formData.date_of_birth);
    const isUnder18 = athleteAge !== null && athleteAge < 18;

    const [availableCities, setAvailableCities] = useState([]);
    const [availableCounties, setAvailableCounties] = useState([]);
    const [availableSubcounties, setAvailableSubcounties] = useState([]);

    const isMyProfile = !id || id === 'me';
    // Age categories with their minimum and maximum age limits
    const allAgeCategories = [
        { value: 'U8', label: 'Under 8', minAge: 0, maxAge: 7 },
        { value: 'U9', label: 'Under 9', minAge: 0, maxAge: 8 },
        { value: 'U10', label: 'Under 10', minAge: 0, maxAge: 9 },
        { value: 'U11', label: 'Under 11', minAge: 0, maxAge: 10 },
        { value: 'U12', label: 'Under 12', minAge: 0, maxAge: 11 },
        { value: 'U13', label: 'Under 13', minAge: 0, maxAge: 12 },
        { value: 'U14', label: 'Under 14', minAge: 0, maxAge: 13 },
        { value: 'U15', label: 'Under 15', minAge: 0, maxAge: 14 },
        { value: 'U16', label: 'Under 16', minAge: 0, maxAge: 15 },
        { value: 'U17', label: 'Under 17', minAge: 0, maxAge: 16 },
        { value: 'U18', label: 'Under 18', minAge: 0, maxAge: 17 },
        { value: 'U19', label: 'Under 19', minAge: 0, maxAge: 18 },
        { value: 'U20', label: 'Under 20', minAge: 0, maxAge: 19 },
        { value: 'U21', label: 'Under 21', minAge: 0, maxAge: 20 },
        { value: 'U23', label: 'Under 23', minAge: 0, maxAge: 22 },
        { value: 'Senior', label: 'Senior (Open)', minAge: 18, maxAge: 100 }
    ];
    const maxSports = user?.role === 'athlete' ? 3 : 10;

    // Get eligible categories based on athlete's age
    // Rule: Athlete can only compete in categories where the category limit is HIGHER than their age
    // Example: 15-year-old can play U16, U17, U18... (category number > age)
    const getEligibleCategories = () => {
        if (athleteAge === null) return allAgeCategories.map(c => c.value);
        
        return allAgeCategories
            .filter(category => athleteAge < category.maxAge + 1) // category limit > age
            .map(c => c.value);
    };

    const ageCategories = getEligibleCategories();

    // Location effects
    useEffect(() => {
        if (formData.country === 'Kenya') {
            setAvailableCounties(Object.keys(kenyaData));
        } else {
            setAvailableCounties([]);
            const countryData = otherCountriesData[formData.country];
            setAvailableCities(countryData?.cities || []);
        }
    }, [formData.country]);

    useEffect(() => {
        if (formData.country === 'Kenya' && formData.county && kenyaData[formData.county]) {
            setAvailableCities(kenyaData[formData.county].cities);
            setAvailableSubcounties(kenyaData[formData.county].subcounties);
        }
    }, [formData.county, formData.country]);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            const sportsRes = await statsApi.getSports();
            setSports(sportsRes.data.sports);

            if (isMyProfile && isAuthenticated) {
                try {
                    const profileRes = await athletesApi.getMyProfile();
                    setAthlete(profileRes.data);
                    populateFormData(profileRes.data);
                } catch (e) {
                    setCreating(true);
                    setFormData(prev => ({ ...prev, full_name: user?.full_name || '' }));
                }
            } else if (id) {
                const athleteRes = await athletesApi.getById(id);
                setAthlete(athleteRes.data);
            }
        } catch (error) {
            console.error('Failed to load profile:', error);
            if (!isMyProfile) {
                toast.error('Profile not found');
                navigate('/talent');
            }
        } finally {
            setLoading(false);
        }
    };

    const populateFormData = (data) => {
        setFormData({
            full_name: data.full_name || '',
            date_of_birth: data.date_of_birth || '',
            age_category: data.age_category || '',
            country: data.country || 'Kenya',
            county: data.county || '',
            subcounty: data.subcounty || '',
            city: data.city || '',
            school: data.school || '',
            academy: data.academy || '',
            sports: data.sports || [],
            mobile_number: data.mobile_number || '',
            bio: data.bio || '',
            profile_image: data.profile_image || '',
            birth_certificate_url: data.birth_certificate_url || '',
            passport_url: data.passport_url || '',
            id_proof: data.id_proof || '',
            parent_guardian_name: data.parent_guardian_name || '',
            parent_guardian_mobile: data.parent_guardian_mobile || '',
            parent_guardian_email: data.parent_guardian_email || '',
            parent_consent: data.parent_consent || false,
            media_consent: data.media_consent || false,
            representing_type: data.representing_type || '',
            representing_name: data.representing_name || '',
            reason_to_register: data.reason_to_register || '',
            profile_location: data.profile_location || null
        });
    };

    const validateForm = () => {
        if (isAthleteRole) {
            // Basic info validation
            if (!formData.full_name) { toast.error('Please enter your full name'); return false; }
            if (!formData.date_of_birth) { toast.error('Please enter your date of birth'); return false; }
            if (!formData.age_category) { toast.error('Please select an age category'); return false; }
            if (!formData.country) { toast.error('Please select your country'); return false; }
            if (!formData.city) { toast.error('Please select your city'); return false; }
            if (formData.sports.length === 0) { toast.error('Please select at least one sport'); return false; }
            
            // Photo validation (mandatory)
            if (!formData.profile_image) { toast.error('Player photo is required'); return false; }
            
            // Document validation: Either birth certificate OR passport required
            if (!formData.birth_certificate_url && !formData.passport_url) { 
                toast.error('Either Birth Certificate or Passport is required for age verification'); 
                return false; 
            }
            
            // Guardian info validation (only for athletes under 18)
            if (isUnder18) {
                if (!formData.parent_guardian_name) { toast.error('Parent/guardian name is required for athletes under 18'); return false; }
                if (!formData.parent_guardian_mobile) { toast.error('Parent/guardian contact number is required for athletes under 18'); return false; }
                if (!formData.parent_guardian_email) { toast.error('Parent/guardian email is required for athletes under 18'); return false; }
                if (!formData.parent_consent) { toast.error('Parent/guardian consent is required for athletes under 18'); return false; }
            }
            
            // Media consent validation (mandatory for ALL athletes)
            if (!formData.media_consent) { toast.error('Media consent is required to participate in events'); return false; }
        } else if (['coach', 'scout', 'organizer'].includes(user?.role)) {
            if (!formData.full_name) { toast.error('Please enter your full name'); return false; }
            if (formData.sports.length === 0) { toast.error('Please select at least one sport'); return false; }
            if (!formData.representing_type) { toast.error('Please select what you represent'); return false; }
            if (!formData.representing_name) { toast.error('Please enter organization name'); return false; }
        } else if (user?.role === 'school_admin') {
            if (!formData.full_name) { toast.error('Please enter your full name'); return false; }
            if (!formData.id_proof) { toast.error('Please upload your ID proof'); return false; }
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        setSaving(true);
        try {
            if (creating) {
                await athletesApi.createProfile(formData);
                toast.success('Profile created successfully!');
                setCreating(false);
            } else {
                await athletesApi.updateProfile(formData);
                toast.success('Profile updated successfully!');
            }
            setEditing(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    // Creating new profile
    if (creating && isMyProfile) {
        return (
            <CreateProfileView
                user={user}
                formData={formData}
                setFormData={setFormData}
                sports={sports}
                saving={saving}
                onSave={handleSave}
                isUnder18={isUnder18}
                ageCategories={ageCategories}
                maxSports={maxSports}
                availableCounties={availableCounties}
                availableSubcounties={availableSubcounties}
                availableCities={availableCities}
                token={token}
                athleteAge={athleteAge}
            />
        );
    }

    // Redirect if not authenticated for own profile
    if (isMyProfile && !isAuthenticated) {
        navigate('/auth');
        return null;
    }

    // Profile not found
    if (!athlete) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <div className="text-center">
                    <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h2 className="font-heading font-bold text-xl uppercase mb-2">Profile Not Found</h2>
                    <p className="text-muted-foreground mb-6">This athlete profile doesn't exist.</p>
                    <Button onClick={() => navigate('/talent')} variant="outline" className="border-white/20">
                        Browse Athletes
                    </Button>
                </div>
            </div>
        );
    }

    // View/Edit Profile
    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            <div className="fixed inset-0 grid-lines pointer-events-none" style={{ zIndex: -1 }} />

            <ProfileHeader
                title={isMyProfile ? 'My Profile' : athlete.full_name}
                backLink={isMyProfile ? '/dashboard' : '/talent'}
                isMyProfile={isMyProfile}
                editing={editing}
                saving={saving}
                onEdit={() => setEditing(true)}
                onCancel={() => setEditing(false)}
                onSave={handleSave}
            />

            <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
                {editing ? (
                    <EditProfileForm
                        formData={formData}
                        setFormData={setFormData}
                        sports={sports}
                        ageCategories={ageCategories}
                        maxSports={maxSports}
                        isUnder18={isUnder18}
                        availableCounties={availableCounties}
                        availableSubcounties={availableSubcounties}
                        availableCities={availableCities}
                        token={token}
                        athleteAge={athleteAge}
                    />
                ) : (
                    <ViewProfile athlete={athlete} isMyProfile={isMyProfile} />
                )}
            </div>
        </div>
    );
};

// Sub-components

const CreateProfileView = ({
    user, formData, setFormData, sports, saving, onSave,
    isUnder18, ageCategories, maxSports, availableCounties,
    availableSubcounties, availableCities, token, athleteAge
}) => {
    const isAthleteRole = user?.role === 'athlete';
    const getRoleDisplayName = (role) => {
        const roleNames = {
            'athlete': 'Athlete', 'coach': 'Coach', 'scout': 'Scout / Recruiter',
            'school_admin': 'School Admin', 'organizer': 'Tournament Organizer'
        };
        return roleNames[role] || role;
    };

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            <div className="fixed inset-0 grid-lines pointer-events-none" style={{ zIndex: -1 }} />
            
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-4 h-4" />
                            </Link>
                            <span className="font-heading font-bold text-lg uppercase">Create Profile</span>
                        </div>
                        <MaraSportsLogo className="h-10" />
                    </div>
                </div>
            </header>

            <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
                <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="w-12 h-12 bg-primary/10 flex items-center justify-center">
                            <User className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-heading font-bold text-2xl uppercase tracking-tight">
                                {isAthleteRole ? 'Create Your Athlete Profile' : `Complete Your ${getRoleDisplayName(user?.role)} Profile`}
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                Fill in your details to complete registration
                            </p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {isAthleteRole ? (
                            <AthleteCreateForm
                                formData={formData}
                                setFormData={setFormData}
                                sports={sports}
                                saving={saving}
                                onSave={onSave}
                                isUnder18={isUnder18}
                                ageCategories={ageCategories}
                                maxSports={maxSports}
                                availableCounties={availableCounties}
                                availableSubcounties={availableSubcounties}
                                availableCities={availableCities}
                                token={token}
                                user={user}
                                athleteAge={athleteAge}
                            />
                        ) : ['coach', 'scout', 'organizer'].includes(user?.role) ? (
                            <NonAthleteCreateForm
                                formData={formData}
                                setFormData={setFormData}
                                sports={sports}
                                saving={saving}
                                onSave={onSave}
                                user={user}
                                token={token}
                            />
                        ) : (
                            <SchoolAdminCreateForm
                                formData={formData}
                                setFormData={setFormData}
                                saving={saving}
                                onSave={onSave}
                                token={token}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const AthleteCreateForm = ({
    formData, setFormData, sports, saving, onSave, isUnder18,
    ageCategories, maxSports, availableCounties, availableSubcounties,
    availableCities, token, user, athleteAge
}) => (
    <>
        {/* Profile Photo Upload - MANDATORY with AI Quality Check */}
        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Player Photo *
                <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded">
                    AI Check
                </span>
            </Label>
            <SmartDocumentUpload
                label="Photo"
                currentDocument={formData.profile_image}
                token={token}
                onDocumentUpdate={(url) => setFormData({ ...formData, profile_image: url })}
                onDataExtracted={(data) => {
                    // Auto-detect gender from photo if not set
                    if (data.gender && !formData.gender) {
                        setFormData(prev => ({ ...prev, gender: data.gender }));
                        toast.info(`Gender detected as ${data.gender} from photo`);
                    }
                    // Show photo quality feedback
                    if (data.photo_quality === 'poor') {
                        toast.warning('Photo quality is poor. Consider uploading a clearer image.');
                    }
                    if (!data.is_face_visible) {
                        toast.warning('Face not clearly visible. Please upload a photo with a clear face.');
                    }
                    if (data.suggestions) {
                        toast.info(data.suggestions, { duration: 5000 });
                    }
                }}
                documentType="photo"
                acceptedTypes=".jpg,.jpeg,.png"
                enableAiScan={true}
                testId="profile-photo-upload"
            />
            <p className="text-xs text-muted-foreground">
                Clear, recent photo of the athlete. AI will verify photo quality. Max 25MB.
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
                <Label className="text-xs tracking-widest uppercase text-muted-foreground">Full Name *</Label>
                <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="bg-[#0a1628] border-white/10 h-11"
                    placeholder="Enter your full name"
                    data-testid="athlete-name-input"
                />
            </div>
            <div className="space-y-2">
                <Label className="text-xs tracking-widest uppercase text-muted-foreground">Date of Birth *</Label>
                <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="bg-[#0a1628] border-white/10 h-11"
                    data-testid="athlete-dob-input"
                />
            </div>
        </div>

        {/* Birth Certificate Upload - with AI Scanning */}
        <div className="space-y-2">
            <div className="flex items-center gap-2 mb-2">
                <span className="text-xs tracking-widest uppercase text-muted-foreground">
                    Identity Document * (Either Birth Certificate OR Passport)
                </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SmartDocumentUpload
                    label="Birth Certificate"
                    currentDocument={formData.birth_certificate_url}
                    token={token}
                    onDocumentUpdate={(url) => setFormData({ ...formData, birth_certificate_url: url })}
                    onDataExtracted={(data) => {
                        // Auto-fill form fields from scanned birth certificate
                        const updates = {};
                        if (data.full_name && !formData.full_name) {
                            updates.full_name = data.full_name;
                        }
                        if (data.date_of_birth && !formData.date_of_birth) {
                            updates.date_of_birth = data.date_of_birth;
                        }
                        if (data.gender && !formData.gender) {
                            updates.gender = data.gender;
                        }
                        if (data.place_of_birth) {
                            if (!formData.city) updates.city = data.place_of_birth;
                        }
                        if (data.country) {
                            const matchedCountry = africanCountries.find(c => 
                                c.toLowerCase().includes(data.country.toLowerCase()) ||
                                data.country.toLowerCase().includes(c.toLowerCase())
                            );
                            if (matchedCountry && !formData.country) {
                                updates.country = matchedCountry;
                            }
                        }
                        
                        if (Object.keys(updates).length > 0) {
                            setFormData(prev => ({ ...prev, ...updates }));
                            toast.success(`Auto-filled ${Object.keys(updates).length} fields from birth certificate!`);
                        }
                    }}
                    documentType="birth_certificate"
                    enableAiScan={true}
                    testId="birth-certificate-upload"
                />
                
                <SmartDocumentUpload
                    label="Passport"
                    currentDocument={formData.passport_url}
                    token={token}
                    onDocumentUpdate={(url) => setFormData({ ...formData, passport_url: url })}
                    onDataExtracted={(data) => {
                        // Auto-fill form fields from scanned passport
                        const updates = {};
                        if (data.full_name && !formData.full_name) {
                            updates.full_name = data.full_name;
                        }
                        if (data.date_of_birth && !formData.date_of_birth) {
                            updates.date_of_birth = data.date_of_birth;
                        }
                        if (data.gender && !formData.gender) {
                            updates.gender = data.gender;
                        }
                        if (data.place_of_birth) {
                            if (!formData.city) updates.city = data.place_of_birth;
                        }
                        if (data.nationality || data.country_of_issue) {
                            const countryName = data.nationality || data.country_of_issue;
                            const matchedCountry = africanCountries.find(c => 
                                c.toLowerCase().includes(countryName.toLowerCase()) ||
                                countryName.toLowerCase().includes(c.toLowerCase())
                            );
                            if (matchedCountry && !formData.country) {
                                updates.country = matchedCountry;
                            }
                        }
                        
                        if (Object.keys(updates).length > 0) {
                            setFormData(prev => ({ ...prev, ...updates }));
                            toast.success(`Auto-filled ${Object.keys(updates).length} fields from passport!`);
                        }
                    }}
                    documentType="passport"
                    enableAiScan={true}
                    testId="passport-upload"
                />
            </div>
            <p className="text-xs text-muted-foreground">
                Upload either a Birth Certificate or Passport for age verification. AI will extract details automatically.
            </p>
        </div>

        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Age Category *</Label>
            {athleteAge !== null && (
                <p className="text-xs text-primary mb-1">
                    Based on your age ({athleteAge} years), you are eligible for: {ageCategories.join(', ')}
                </p>
            )}
            <Select value={formData.age_category} onValueChange={(v) => setFormData({ ...formData, age_category: v })}>
                <SelectTrigger className="bg-[#0a1628] border-white/10 h-11" data-testid="age-category-select">
                    <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-[#0d2847] border-white/10">
                    {ageCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {!formData.date_of_birth && (
                <p className="text-xs text-muted-foreground">Enter date of birth first to see eligible categories</p>
            )}
        </div>

        <LocationFields
            formData={formData}
            setFormData={setFormData}
            availableCounties={availableCounties}
            availableSubcounties={availableSubcounties}
            availableCities={availableCities}
        />

        <SportSelector
            sports={sports}
            selectedSports={formData.sports}
            onToggleSport={(newSports) => setFormData({ ...formData, sports: newSports })}
            maxSports={maxSports}
        />

        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Mobile Number</Label>
            <Input
                value={formData.mobile_number}
                onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                className="bg-[#0a1628] border-white/10 h-11"
                placeholder="+254 7XX XXX XXX"
                data-testid="athlete-mobile-input"
            />
        </div>

        {/* Guardian Information - only for athletes under 18 */}
        <ParentGuardianFields
            formData={formData}
            setFormData={setFormData}
            isUnder18={isUnder18}
        />

        {/* Media Consent - MANDATORY */}
        <MediaConsentField
            formData={formData}
            setFormData={setFormData}
        />

        {/* Location Capture - Optional but recommended */}
        <LocationPermissionPrompt
            onLocationCaptured={(locationData) => {
                if (locationData) {
                    setFormData(prev => ({ ...prev, profile_location: locationData }));
                }
            }}
            context="profile"
            showSkip={true}
            className="mt-4"
        />

        <Button
            onClick={onSave}
            disabled={saving}
            className="w-full bg-primary hover:bg-cyan-400 text-[#0a1628] btn-glow uppercase tracking-widest text-sm font-bold h-12"
            data-testid="create-profile-btn"
        >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Profile
        </Button>
    </>
);

const NonAthleteCreateForm = ({ formData, setFormData, sports, saving, onSave, user, token }) => (
    <>
        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Full Name *</Label>
            <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="bg-[#0a1628] border-white/10 h-11"
            />
        </div>

        <SportSelector
            sports={sports}
            selectedSports={formData.sports}
            onToggleSport={(newSports) => setFormData({ ...formData, sports: newSports })}
            maxSports={10}
        />

        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Representing *</Label>
            <Select value={formData.representing_type} onValueChange={(v) => setFormData({ ...formData, representing_type: v })}>
                <SelectTrigger className="bg-[#0a1628] border-white/10 h-11">
                    <SelectValue placeholder="Select what you represent" />
                </SelectTrigger>
                <SelectContent className="bg-[#0d2847] border-white/10">
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="academy">Academy</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="others">Others</SelectItem>
                </SelectContent>
            </Select>
        </div>

        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Organization Name *</Label>
            <Input
                value={formData.representing_name}
                onChange={(e) => setFormData({ ...formData, representing_name: e.target.value })}
                className="bg-[#0a1628] border-white/10 h-11"
            />
        </div>

        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Reason to Register *</Label>
            <Textarea
                value={formData.reason_to_register}
                onChange={(e) => setFormData({ ...formData, reason_to_register: e.target.value })}
                className="bg-[#0a1628] border-white/10 min-h-[120px]"
                placeholder="Tell us why you want to register..."
            />
        </div>

        <Button onClick={onSave} disabled={saving} className="w-full bg-primary hover:bg-cyan-400 text-[#0a1628] h-12">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Complete Profile
        </Button>
    </>
);

const SchoolAdminCreateForm = ({ formData, setFormData, saving, onSave, token }) => (
    <>
        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Full Name *</Label>
            <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="bg-[#0a1628] border-white/10 h-11"
            />
        </div>

        <DocumentUpload
            label="ID Proof"
            currentDocument={formData.id_proof}
            token={token}
            onDocumentUpdate={(url) => setFormData({ ...formData, id_proof: url })}
            endpoint="/api/athletes/upload-document"
            testId="id-proof-upload"
        />

        <Button onClick={onSave} disabled={saving} className="w-full bg-primary hover:bg-cyan-400 text-[#0a1628] h-12">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Complete Profile
        </Button>
    </>
);

const EditProfileForm = ({
    formData, setFormData, sports, ageCategories, maxSports, isUnder18,
    availableCounties, availableSubcounties, availableCities, token, athleteAge
}) => (
    <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8 space-y-6">
        <h2 className="font-heading font-bold text-xl uppercase mb-6">Edit Profile</h2>

        {/* Profile Photo Upload */}
        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Player Photo *
            </Label>
            <ProfilePhotoUpload
                currentPhoto={formData.profile_image}
                token={token}
                onPhotoUpdate={(url) => setFormData({ ...formData, profile_image: url })}
            />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
                <Label className="text-xs tracking-widest uppercase text-muted-foreground">Full Name *</Label>
                <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="bg-[#0a1628] border-white/10 h-11"
                />
            </div>
            <div className="space-y-2">
                <Label className="text-xs tracking-widest uppercase text-muted-foreground">Date of Birth</Label>
                <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="bg-[#0a1628] border-white/10 h-11"
                />
            </div>
        </div>

        {/* Birth Certificate Upload */}
        <DocumentUpload
            label="Birth Certificate *"
            currentDocument={formData.birth_certificate_url}
            token={token}
            onDocumentUpdate={(url) => setFormData({ ...formData, birth_certificate_url: url })}
            endpoint="/api/athletes/upload-document"
            testId="birth-certificate-upload"
        />

        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Age Category</Label>
            {athleteAge !== null && (
                <p className="text-xs text-primary mb-1">
                    Based on your age ({athleteAge} years), eligible for: {ageCategories.join(', ')}
                </p>
            )}
            <Select value={formData.age_category} onValueChange={(v) => setFormData({ ...formData, age_category: v })}>
                <SelectTrigger className="bg-[#0a1628] border-white/10 h-11">
                    <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-[#0d2847] border-white/10">
                    {ageCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>

        <LocationFields
            formData={formData}
            setFormData={setFormData}
            availableCounties={availableCounties}
            availableSubcounties={availableSubcounties}
            availableCities={availableCities}
        />

        <SportSelector
            sports={sports}
            selectedSports={formData.sports}
            onToggleSport={(newSports) => setFormData({ ...formData, sports: newSports })}
            maxSports={maxSports}
        />

        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Bio</Label>
            <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="bg-[#0a1628] border-white/10 min-h-[100px]"
                placeholder="Tell us about yourself..."
            />
        </div>

        {/* Guardian Information - only for under 18 */}
        <ParentGuardianFields formData={formData} setFormData={setFormData} isUnder18={isUnder18} />
        
        {/* Media Consent - mandatory */}
        <MediaConsentField formData={formData} setFormData={setFormData} />
    </div>
);

const ViewProfile = ({ athlete, isMyProfile }) => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
            <ProfileDetailsView athlete={athlete} />
        </div>
        <div className="space-y-6">
            <div className="bg-[#0d2847] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-sm uppercase mb-4">Profile Photo</h3>
                <div className="w-full aspect-square bg-[#0a1628] border border-white/10 overflow-hidden">
                    {athlete.profile_image ? (
                        <img src={athlete.profile_image} alt={athlete.full_name} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <User className="w-20 h-20 text-muted-foreground" />
                        </div>
                    )}
                </div>
            </div>
            {athlete.verified && (
                <div className="bg-green-500/10 border border-green-500/30 p-4">
                    <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-green-400" />
                        <span className="font-semibold text-green-400">Verified Athlete</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                        This athlete's identity has been verified
                    </p>
                </div>
            )}
            {athlete.national_athlete_id && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 p-4" data-testid="national-athlete-id-display">
                    <div className="flex items-center gap-2">
                        <Award className="w-5 h-5 text-cyan-400" />
                        <span className="font-semibold text-cyan-400">National Athlete ID</span>
                    </div>
                    <p className="text-lg font-mono font-bold text-white mt-1" data-testid="national-athlete-id-value">
                        {athlete.national_athlete_id}
                    </p>
                </div>
            )}
        </div>
    </div>
);

export default AthleteProfilePage;
